import React, { useRef, useMemo } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './RichTextEditor.css';

// ===== EXTENDED INDENT CONFIGURATION =====
// Extend Quill's default indent levels from ~8 to 10
// This must happen AFTER ReactQuill is imported but BEFORE any editor is created
try {
  const IndentClass = Quill.import('attributors/class/indent');
  if (IndentClass) {
    IndentClass.whitelist = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
    Quill.register(IndentClass, true);
  }
} catch (error) {
  // Indent attributor may not be immediately available; Quill will use defaults
  // CSS rules for .ql-indent-1 through .ql-indent-10 still apply
}
// ===== END EXTENDED INDENT CONFIGURATION =====

function RichTextEditor({
  value = '',
  onChange,
  placeholder = 'Start typing...',
  maxLength = 50000,
  error = null,
  disabled = false,
  id = 'rich-text-editor'
}) {
  const quillRef = useRef(null);
  
  // Get plain text length (without HTML tags)
  const getTextLength = (html) => {
    if (!html) return 0;
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return temp.textContent.length || 0;
  };

  const currentLength = getTextLength(value);
  const isOverLimit = currentLength > maxLength;

  // Quill toolbar configuration
  const modules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      ['blockquote', 'code-block'],
      ['link'],
      ['clean']
    ],
    clipboard: {
      matchVisual: false
    }
  }), []);

  const formats = [
    'header',
    'bold', 'italic', 'underline',
    'list', 'bullet',
    'indent',
    'blockquote', 'code-block',
    'link'
  ];

  const handleChange = (content, delta, source, editor) => {
    const textLength = editor.getLength() - 1; // Quill adds 1 for newline
    
    if (textLength <= maxLength) {
      onChange(content);
    }
  };

  return (
    <div className={`rich-text-editor ${error ? 'has-error' : ''}`}>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={handleChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        readOnly={disabled}
        id={id}
        aria-label="Content editor"
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : `${id}-counter`}
      />
      
      <div className="editor-footer">
        <div 
          id={`${id}-counter`}
          className={`character-counter ${isOverLimit ? 'over-limit' : ''}`}
          aria-live="polite"
          aria-atomic="true"
        >
          {currentLength.toLocaleString()} / {maxLength.toLocaleString()} characters
        </div>
      </div>

      {error && (
        <div 
          id={`${id}-error`}
          className="field-error" 
          role="alert"
        >
          {error}
        </div>
      )}
    </div>
  );
}

export default RichTextEditor;
